// This file configures the initialization of PostHog on the client.
// The config here will be used whenever a user loads a page in their browser.
//
// PostHog is deferred until after the page is interactive to reduce TBT.

import { analiticaActivaEnNavegador } from './lib/analytics/entorno';

function initPostHog() {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  // Sin clave no hay nada que inicializar: posthog.init(undefined) no lanza,
  // solo avisa por consola ("initialized without a token") en cada carga. En
  // local no solemos tener la clave, así que salimos antes.
  if (!posthogKey) return;
  // staging y qa usan la misma clave: sin esto grababan sesiones de prueba.
  if (!analiticaActivaEnNavegador()) return;

  import('posthog-js').then(({ default: posthog }) => {
    try {
      if (posthog.__loaded) return; // already initialized
      posthog.init(posthogKey, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        defaults: '2026-01-30',
        debug: process.env.NODE_ENV === 'development',

        // Always create a person profile so anon sessions still attach to a
        // replay when the user later logs in — identify() merges both.
        person_profiles: 'always',

        // App Router pageviews (history.pushState instead of full loads).
        capture_pageview: 'history_change',
        capture_pageleave: true,
        autocapture: true,

        // Rage clicks → fires $rageclick events used by frustration filters.
        rageclick: true,

        // Console logs travel with the replay. Off by default; opt-in here.
        enable_recording_console_log: true,

        // Session Replay — capture everything the product team could want to
        // understand a bug report. Password fields mask automatically; other
        // sensitive inputs can opt in via `data-ph-mask` or `.ph-no-capture`.
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: { password: true },
          maskTextSelector: '[data-ph-mask]',
          blockSelector: '.ph-no-capture',

          // Network tab inside the replay: capture fetch/XHR URLs, headers,
          // request + response bodies, and performance entries. PostHog
          // monkey-patches fetch/XHR itself once these flags are on.
          recordHeaders: true,
          recordBody: true,
          // @ts-expect-error — `recordPerformance` ships in posthog-js but
          // its TypeScript types haven't caught up yet (tracked upstream).
          // Remove this directive once @types catches up.
          recordPerformance: true,

          // Redact obvious password / token fields from captured payloads
          // before they leave the browser. Anything scrubbed here NEVER
          // reaches PostHog — add more patterns as new sensitive keys show
          // up, or switch specific hosts to `null` to drop entire requests.
          maskCapturedNetworkRequestFn: (req) => {
            const scrub = (body: string | undefined) =>
              body
                ? body
                    .replace(/"(password|currentPassword|newPassword)":\s*"[^"]*"/gi, '"$1":"***"')
                    .replace(/"(token|accessToken|refreshToken|apiKey|secret)":\s*"[^"]*"/gi, '"$1":"***"')
                : body;
            if (req.requestBody) req.requestBody = scrub(req.requestBody);
            if (req.responseBody) req.responseBody = scrub(req.responseBody);
            return req;
          },
        },

        // Persistence
        persistence: 'localStorage+cookie',
      });
      // Safety net for consumers that can't afford a dynamic import at call
      // time (e.g. ticket-creation metadata extraction) — they peek here
      // instead of bundling posthog-js eagerly.
      (globalThis as unknown as { __ezdocu_posthog__?: unknown }).__ezdocu_posthog__ = posthog;
    } catch {
      // PostHog init may fail — fail silently
    }
  }).catch(() => {
    // PostHog import failed — fail silently
  });
}

// ── Schedule deferred init: first interaction OR idle timeout ────────
let analyticsScheduled = false;
function scheduleAnalytics() {
  if (analyticsScheduled) return;
  analyticsScheduled = true;

  const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
  let idleTimer: ReturnType<typeof setTimeout>;

  function onInteraction() {
    clearTimeout(idleTimer);
    events.forEach(e => document.removeEventListener(e, onInteraction));
    initPostHog();
  }

  events.forEach(e => document.addEventListener(e, onInteraction, { once: true, passive: true }));

  // Fallback: init after 3.5s if no interaction
  idleTimer = setTimeout(() => {
    events.forEach(e => document.removeEventListener(e, onInteraction));
    initPostHog();
  }, 3500);
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'complete') {
    scheduleAnalytics();
  } else {
    window.addEventListener('load', scheduleAnalytics, { once: true });
  }
}
